"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get FindIMWorkersByFiltersResultModel () {
        return FindIMWorkersByFiltersResultModel;
    },
    get IMWorkerFieldFilterModel () {
        return IMWorkerFieldFilterModel;
    },
    get IMWorkerModel () {
        return IMWorkerModel;
    },
    get SCIM_CUSTOM_FIELD_NAMES () {
        return SCIM_CUSTOM_FIELD_NAMES;
    },
    get SCIM_ENTERPRISE_USER_SCHEMA () {
        return SCIM_ENTERPRISE_USER_SCHEMA;
    },
    get SCIM_LIST_RESPONSE_SCHEMA () {
        return SCIM_LIST_RESPONSE_SCHEMA;
    },
    get SCIM_PATCH_OP_SCHEMA () {
        return SCIM_PATCH_OP_SCHEMA;
    },
    get SCIM_USER_SCHEMA () {
        return SCIM_USER_SCHEMA;
    },
    get ScimUserModel () {
        return ScimUserModel;
    }
});
const _bson = require("bson");
const _zod = require("zod");
const SCIM_USER_SCHEMA = "urn:ietf:params:scim:schemas:core:2.0:User";
const SCIM_LIST_RESPONSE_SCHEMA = "urn:ietf:params:scim:api:messages:2.0:ListResponse";
const SCIM_ENTERPRISE_USER_SCHEMA = "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User";
const SCIM_PATCH_OP_SCHEMA = "urn:ietf:params:scim:api:messages:2.0:PatchOp";
const ScimSchema = _zod.z.enum([
    SCIM_USER_SCHEMA,
    SCIM_LIST_RESPONSE_SCHEMA,
    SCIM_ENTERPRISE_USER_SCHEMA,
    SCIM_PATCH_OP_SCHEMA
]);
const SCIM_CUSTOM_FIELD_NAMES = [
    "First Name",
    "Last Name",
    "Activation Status",
    "Email",
    "Job Title",
    "Nickname",
    "Manager"
];
const ScimUserModel = _zod.z.object({
    /** The ID of the im_worker document. **/ id: _zod.z.string().optional(),
    /**
	 * The schemas attribute is REQUIRED and specifies the list of SCIM schemas
	 * that are used to indicate the namespaces of the SCIM objects in the current
	 * representation. Each schema URI MUST be unique. Order is not specified and
	 * MUST NOT impact behavior.
	 * e.g. ["urn:ietf:params:scim:schemas:core:2.0:User"] for a User object.
	 * e.g. ["urn:ietf:params:scim:schemas:core:2.0:Group"] for a Group object.
	 * e.g. ["urn:ietf:params:scim:schemas:core:2.0:User", "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User"] for a User object with an enterprise extension.
	 */ schemas: _zod.z.array(ScimSchema),
    /** This is the id from the client's system. Clients aren't required to send an
	 * externalId, and we should never manipulate the value outside of changes we
	 * may receive from the SCIM client.
	 **/ externalId: _zod.z.string().optional(),
    /**
	 * The "meta" attribute is a complex attribute containing resource metadata. All
	 * sub-attributes are assigned by us, the service provider.
	 **/ meta: _zod.z.object({
        /**
			 * non-empty string that specifies the resource type being returned (e.g., "User", "Group").
			 **/ resourceType: _zod.z.string().optional(),
        // This is useful information, but I don't believe we capture it on our user documents
        created: _zod.z.string().optional(),
        lastModified: _zod.z.string().optional(),
        location: _zod.z.string().optional(),
        version: _zod.z.string().optional()
    }).optional(),
    /**
	 * The "userName" attribute is a REQUIRED non-empty string. This identifier MUST be
	 * unique across our, the service provider's, entire set of Users. It MUST be a
	 * stable value and is case insensitive.
	 * Often used as the login identifier for the User, such as a username, email address..
	 * In our case, this is the user's email address. We break the rule of "unique across
	 * the service provider's entire set of Users" here because users are allowed to have
	 * multiple PerformYard accounts with the same email address.
	 * This should be ok as long as we guarantee that we filter by companyId in our queries.
	 **/ userName: _zod.z.string(),
    /**
	 * The "name" attribute is a complex attribute containing the components of the
	 * user's real name. The formatted sub-attribute is the attribute we will provide.
	 * The others may be provided by a client, but since we only have a single name
	 * field in our user documents, we will combine them into a single formatted field
	 * if the formatted field is not provided by the client.
	 **/ name: _zod.z.object({
        /** Full name, including all middle names, titles, and suffixes as appropriate **/ formatted: _zod.z.string().optional(),
        /** The family name of the User, or "Last Name" in most Western languages **/ familyName: _zod.z.string().optional(),
        /** The given name of the User, or "First Name" in most Western languages **/ givenName: _zod.z.string().optional(),
        /** The middle name(s) of the User **/ middleName: _zod.z.string().optional(),
        /** The honorific prefix(es) of the User, or "Title" in most Western languages **/ honorificPrefix: _zod.z.string().optional(),
        /** The honorific suffix(es) of the User, or "Suffix" in most Western languages **/ honorificSuffix: _zod.z.string().optional()
    }).optional(),
    /**
	 * The casual way to address the user in real life, e.g., "Bob" or
	 * "Bobby" instead of "Robert".
	 **/ nickName: _zod.z.string().optional(),
    /**
	 * The User's title, such as "Vice President".
	 **/ title: _zod.z.string().optional(),
    /**
	 * The value provided SHOULD be the primary textual
	 * label by which this User is normally displayed by the service
	 * provider when presenting it to end-users
	 **/ displayName: _zod.z.string().optional(),
    /**
	 * Boolean value indicating the User's administrative status. We, the service provider, interpret
	 * the meaning of this value. It should directly correspond with the "deactivated" field on our
	 * user documents.
	 * If true, the User's administrative status is active.
	 * If false, the User's administrative status is inactive.
	 * If omitted, the User's administrative status is active.
	 **/ active: _zod.z.boolean().optional(),
    /**
	 * The "emails" attribute is a multi-valued complex attribute containing
	 * e-mail addresses for the User. We require at least one email address.
	 **/ emails: _zod.z.array(_zod.z.object({
        /**
				 * Canonical representation of the email address
				 * eg. "rick@example.com" instead of "Rick@Example.com"
				 **/ value: _zod.z.string(),
        /** A label indicating the attribute's function, e.g., 'work',home' or 'other'. **/ type: _zod.z.string(),
        /** Boolean value indicating the 'primary' or preferred attribute value for this attribute **/ primary: _zod.z.boolean()
    })).optional(),
    roles: _zod.z.array(_zod.z.string()).optional(),
    enterprise: _zod.z.object({
        manager: _zod.z.object({
            value: _zod.z.string().optional(),
            $ref: _zod.z.string().optional(),
            displayName: _zod.z.string().optional()
        }).optional()
    }).passthrough().optional()
});
const IMWorkerModel = ScimUserModel.omit({
    id: true
}).extend({
    _id: _zod.z.instanceof(_bson.ObjectId),
    companyId: _zod.z.instanceof(_bson.ObjectId),
    updated_at: _zod.z.date().optional(),
    // This represents the enterprise extension schema. We don't use the enterprise extension
    // schema name directly because colons aren't well supported key names in MongoDB.
    enterprise: _zod.z.object({
        manager: _zod.z.object({
            value: _zod.z.string().optional(),
            $ref: _zod.z.string().optional(),
            displayName: _zod.z.string().optional()
        }).optional()
    }).passthrough().optional()
});
const FindIMWorkersByFiltersResultModel = _zod.z.object({
    workers: _zod.z.array(_zod.z.object({
        _id: _zod.z.instanceof(_bson.ObjectId),
        email: _zod.z.string(),
        name: _zod.z.object({
            formatted: _zod.z.string().optional(),
            familyName: _zod.z.string().optional(),
            givenName: _zod.z.string().optional()
        }).optional(),
        emails: _zod.z.array(_zod.z.object({
            value: _zod.z.string(),
            type: _zod.z.string(),
            primary: _zod.z.boolean()
        })).optional(),
        deactivated: _zod.z.object({}).optional()
    })),
    total: _zod.z.number()
});
const IMWorkerFieldFilterModel = _zod.z.object({
    filterKey: _zod.z.string(),
    filterValue: _zod.z.string(),
    filterValueType: _zod.z.string().optional()
});
